'use client';

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { supabase } from '../supabaseClient';
import {
  ExclamationTriangleIcon,
  HomeIcon,
  MapIcon,
  FolderIcon,
  ArrowUpTrayIcon,
  ComputerDesktopIcon,
  Cog8ToothIcon,
  LockClosedIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import darkLogo from '../assets/appLogoFinalDark.png';
import { AlertBanner } from '../components/TrialBanner';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Measurements', href: '/measurements', icon: MapIcon },
  { name: 'Projects', href: '/projects', icon: FolderIcon },
  { name: 'Upload', href: '/upload', icon: ArrowUpTrayIcon },
  { name: 'HQ Mode', href: '/measurementhq', icon: ComputerDesktopIcon },
  { name: 'Settings', href: '/settings', icon: Cog8ToothIcon },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Layout({ children, noPadding = false, noScroll = false }) {
  const { isAuthenticated, loading: authLoading } = useContext(AuthContext);
  const { 
    user, 
    profile, 
    organization,
    subscriptionInfo, 
    loading: userLoading,
    role // <--- UPDATED: Destructure 'role' from the user context
  } = useUser();

  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved !== null ? JSON.parse(saved) : false;
    }
    return false;
  });
  const [currentPath, setCurrentPath] = useState('');
  const [emailConfirmed, setEmailConfirmed] = useState(profile?.is_email_confirmed ?? false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otpError, setOtpError] = useState('');
  const [loadingAction, setLoadingAction] = useState('');
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const hasActiveSubscription = useMemo(() => {
    if (!subscriptionInfo) return false;
    const now = new Date();
    const daysUntilTrialEnd = subscriptionInfo.trialEndDate
      ? Math.ceil((new Date(subscriptionInfo.trialEndDate) - now) / (1000 * 60 * 60 * 24))
      : 0;
    return (subscriptionInfo.status === 'active' || (subscriptionInfo.status === 'trialing' && daysUntilTrialEnd > 0));
  }, [subscriptionInfo]);

  // Updated lockout condition: It will NOT lock out the user if they are on the billing page.
  const isLockoutActive = !hasActiveSubscription && (subscriptionInfo?.status === 'trialing' || subscriptionInfo?.status === 'past_due');
  const shouldLockout = isLockoutActive && !location.pathname.startsWith('/settings/Billing');
  
  // const shouldLockout = true && !location.pathname.startsWith('/settings/Billing');
  
  const isMemberInOrganization = !!organization;
  const isEmailUnconfirmed = user && !emailConfirmed;

  useEffect(() => {
    if (profile) {
      setEmailConfirmed(profile.is_email_confirmed);
    }
  }, [profile]);
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      if (location.pathname !== '/') {
        navigate('/');
      }
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname]);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const savedCollapsedState = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsedState !== null) {
      const saved = JSON.parse(savedCollapsedState);
      if (saved !== sidebarCollapsed) {
        setSidebarCollapsed(saved);
      }
    }
  }, []);

  const handleUpgrade = () => {
    navigate('/settings/Billing');
  };

  const handleManageMembers = () => {
    navigate('/settings/Team');
  };

  const toggleSidebarCollapse = () => {
    const newCollapsedState = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsedState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
  };
  
  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const closeSidebar = () => {
    if (sidebarOpen) {
      setSidebarOpen(false);
    }
  };
  
  const handleTooltipEnter = (e, content) => {
    // If the sidebar is expanded, only show a tooltip if the item is disabled.
    // In that case, we only need to show the reason it's disabled.
    if (!sidebarCollapsed) {
      if (!content.subtitle) {
        return; // Don't show tooltips for enabled items when sidebar is expanded.
      }
      // For disabled items, reformat the content to only show the subtitle.
      content = { text: content.subtitle };
    }
    
    // For a collapsed sidebar, or a disabled item in an expanded sidebar, calculate position and show the tooltip.
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({ x: rect.right + 8, y: rect.top + rect.height / 2 });
    setHoveredTooltip(content);
  };

  const handleTooltipLeave = () => {
    setHoveredTooltip(null);
  };

  const handleSendConfirmationEmail = async () => {
    try {
      setOtpError('');
      setOtpMessage('');
      setLoadingAction('send');
      const { error } = await supabase.auth.signInWithOtp({
        email: user.email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) {
        console.error('Error sending OTP:', error.message);
        setOtpError(`Failed to send code: ${error.message}`);
      } else {
        setOtpMessage('We just sent a code to your email. Enter it below to confirm.');
        setOtpStep(true);
      }
    } catch (err) {
      console.error('Unexpected error sending OTP:', err.message);
      setOtpError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoadingAction('');
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setOtpError('');
      setOtpMessage('');
      setLoadingAction('verify');
      const { error } = await supabase.auth.verifyOtp({
        email: user.email,
        token: otp.trim(),
        type: 'email',
      });
      if (error) {
        console.error('OTP verification failed:', error.message);
        setOtpError(`Code verification failed: ${error.message}`);
      } else {
        setOtpMessage('Your email has been confirmed!');
        const { error: updateError } = await supabase
          .from('profile')
          .update({ is_email_confirmed: true })
          .eq('id', user.id);
        if (updateError) {
          console.error('Error updating email confirmation in profile:', updateError.message);
        }
        setEmailConfirmed(true);
        setOtp('');
        setOtpStep(false);
      }
    } catch (err) {
      console.error('Unexpected error verifying OTP:', err.message);
      setOtpError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingAction('');
    }
  };

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={`flex h-screen overflow-x-hidden ${noScroll ? 'overflow-y-hidden' : ''}`} onClick={closeSidebar}>
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-10 flex h-12 items-center bg-gray-900 text-white md:hidden">
        <button onClick={(e) => { e.stopPropagation(); setSidebarOpen(!sidebarOpen); }} className="ml-4">
          {sidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={classNames(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-gray-950 text-white transition-all duration-300 ease-in-out border-r border-gray-800',
          sidebarOpen ? 'translate-x-0 w-56' : '-translate-x-full w-56',
          'md:relative md:translate-x-0',
          sidebarCollapsed ? 'md:w-16' : 'md:w-52'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-gray-800 relative overflow-hidden">
          <div className="hidden md:flex md:items-center md:justify-between md:w-full">
            <div className={classNames("flex items-center justify-between w-full transition-all duration-300", sidebarCollapsed ? "opacity-0 scale-95" : "opacity-100 scale-100")} style={{ position: !sidebarCollapsed ? 'relative' : 'absolute', visibility: !sidebarCollapsed ? 'visible' : 'hidden', transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <img alt="TransTech" src={darkLogo} className="h-4 w-auto" />
              <button onClick={toggleSidebarCollapse} className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-800/50 text-gray-400 transition-all hover:bg-gray-700 hover:text-white">
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            <button onClick={toggleSidebarCollapse} className={classNames("h-10 flex items-center justify-center rounded-lg bg-gray-800/50 text-gray-400 transition-all hover:bg-gray-700 hover:text-white", sidebarCollapsed ? "opacity-100 scale-100 w-full" : "opacity-0 scale-95 w-0")} style={{ position: sidebarCollapsed ? 'relative' : 'absolute', visibility: sidebarCollapsed ? 'visible' : 'hidden', transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          </div>
          <div className="md:hidden">
            <img alt="TransTech" src={darkLogo} className="h-auto w-32" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            <ul className="space-y-1">
            {navigation.map((item) => {
              // Determine the two separate reasons a link might be disabled.
              const isGatedByTeam = (item.name === 'Projects' || item.name === 'Upload' || item.name === 'HQ Mode') && !isMemberInOrganization;
              const isGatedByLockout = shouldLockout && item.name !== 'Settings';

              // A link is disabled if either condition is true.
              const isDisabled = isGatedByTeam || isGatedByLockout;

              // Determine the correct tooltip message based on the reason.
              let tooltipSubtitle = null;
              if (isGatedByLockout) {
                tooltipSubtitle = 'Update subscription to access';
              } else if (isGatedByTeam) {
                tooltipSubtitle = 'Create or join a team to access';
              }
              
              // A link is only "active" if it's not disabled and the path matches.
              const isActive = !isDisabled && currentPath.startsWith(item.href);

              return (
                <li key={item.name} className="relative">
                  <Link
                    to={item.href}
                    className={classNames(isDisabled ? 'text-gray-500 cursor-not-allowed opacity-50' : isActive ? 'bg-gray-800 text-white shadow-lg shadow-gray-900/50' : 'text-gray-300 hover:bg-gray-800/50 hover:text-white', 'group flex rounded-lg text-sm font-medium items-center transition-all duration-300 relative')}
                    style={{ paddingLeft: sidebarCollapsed ? '0.5rem' : '0.75rem', paddingRight: sidebarCollapsed ? '0.5rem' : '0.75rem', paddingTop: '0.375rem', paddingBottom: '0.375rem', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)', ...(isActive && { transform: 'translateY(-1px) scale(1.01)', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.15)', })}}
                    onClick={(e) => isDisabled && e.preventDefault()}
                    onMouseEnter={(e) => handleTooltipEnter(e, { text: item.name, subtitle: tooltipSubtitle })}
                    onMouseLeave={handleTooltipLeave}
                  >
                    <item.icon aria-hidden="true" className={classNames("h-4 w-4 shrink-0 transition-all duration-300", isActive ? "text-white drop-shadow-md" : "text-gray-400")} style={{ marginRight: sidebarCollapsed ? '0' : '0.75rem', transition: 'margin 300ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
                    <span className={classNames('truncate transition-all duration-300', sidebarCollapsed ? 'md:w-0 md:opacity-0' : 'md:w-auto md:opacity-100', isActive ? 'font-semibold' : '')} style={{ transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      {item.name}
                    </span>
                    {isDisabled && !sidebarCollapsed && (
                      <LockClosedIcon className="h-4 w-4 text-gray-500 ml-auto transition-opacity duration-300" aria-hidden="true" />
                    )}
                    {isActive && (<div className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-r-full shadow-lg shadow-yellow-500/30" />)}
                  </Link>
                </li>
              );
            })}
          </ul>
          </ul>
        </nav>

        {/* Footer */}
      <div className="border-t border-gray-800 p-3">
        {isEmailUnconfirmed && (
          <div className={classNames('mb-3 rounded-lg bg-gradient-to-r from-red-900/80 to-red-800/80 shadow-md transition-all duration-300', sidebarCollapsed ? 'md:opacity-0 md:scale-95 md:h-0 md:overflow-hidden md:mb-0' : 'md:opacity-100 md:scale-100 p-3')} style={{ transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
            {!otpStep ? (
              <div className="space-y-2">
                <div className="flex items-center gap-x-2"><ExclamationTriangleIcon className="h-4 w-4 text-red-400 animate-pulse" /><p className="text-xs font-semibold text-red-200">Email Confirmation Required</p></div>
                <p className="text-xs text-red-300 leading-relaxed">Confirm your email to secure your account.</p>
                <button onClick={handleSendConfirmationEmail} disabled={loadingAction === 'send'} className="w-full rounded-md bg-red-600 py-1.5 text-xs font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed">
                  {loadingAction === 'send' ? 'Sending...' : 'Send Confirmation Code'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-red-200">Enter Your Confirmation Code</p>
                <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="w-full rounded-md border border-gray-600 bg-gray-900 p-1.5 text-xs text-white placeholder-gray-500 focus:border-red-400 focus:ring-1 focus:ring-red-400 focus:outline-none" disabled={loadingAction === 'verify'}/>
                <button onClick={handleVerifyOtp} disabled={loadingAction === 'verify' || !otp.trim()} className="w-full rounded-md bg-red-600 py-1.5 text-xs font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loadingAction === 'verify' ? 'Verifying...' : 'Verify'}
                </button>
                <div className="text-xs text-red-300"><button onClick={handleSendConfirmationEmail} className="underline hover:text-red-100" disabled={loadingAction === 'send'}>Resend code</button></div>
                {otpError && <p className="text-xs text-red-400">{otpError}</p>}
                {otpMessage && <p className="text-xs text-green-400">{otpMessage}</p>}
              </div>
            )}
          </div>
        )}

        {isEmailUnconfirmed && (
          <div className={classNames('flex justify-center mb-3 transition-all duration-300', sidebarCollapsed ? 'md:opacity-100 md:scale-100' : 'md:opacity-0 md:scale-0 md:h-0')} style={{ display: sidebarCollapsed ? 'flex' : 'none', transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)'}}>
            <div onMouseEnter={(e) => handleTooltipEnter(e, { text: 'Email confirmation required' })} onMouseLeave={handleTooltipLeave} className="h-8 w-8 rounded-full bg-red-600 flex items-center justify-center animate-pulse cursor-pointer">
              <ExclamationTriangleIcon className="h-5 w-5 text-white" />
            </div>
          </div>
        )}

        {/* --- UPDATED: User Profile Section is now conditionally rendered --- */}
        {!sidebarCollapsed && (
          <div className={classNames('flex items-center gap-x-3 mb-3 transition-all duration-300')} style={{ flexDirection: 'row', gap: '0.75rem', alignItems: 'flex-start', transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)'}}>
            <div className={classNames('flex-1 min-w-0 transition-all duration-300')} style={{ textAlign: 'left', transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)'}}>
              {profile ? (
                <>
                  <p className={classNames('text-sm font-medium text-white truncate transition-all duration-300', 'md:opacity-100')}>{profile.first_name} {profile.last_name}</p>
                  <a className={classNames('text-xs text-gray-400 hover:text-gray-300 transition-all duration-300', 'md:opacity-100')} href="/settings/account">View profile</a>
                </>
              ) : (<p className={classNames('text-sm text-gray-400 transition-all duration-300', 'md:opacity-100')}>Loading...</p>)}
            </div>
          </div>
        )}
        
        {/* --- UPDATED: Logout Button Logic --- */}
        {user && (
          <button onClick={handleLogout} className={classNames('rounded-lg bg-red-600 text-sm font-medium text-white hover:bg-red-500 transition-all duration-300 flex items-center justify-center')} style={{ width: sidebarCollapsed ? '2.5rem' : '100%', height: sidebarCollapsed ? '2.5rem' : 'auto', padding: sidebarCollapsed ? '0' : '0.5rem 1rem', transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)'}} title={sidebarCollapsed ? 'Logout' : ''}>
            <span className={classNames('transition-all duration-300', sidebarCollapsed ? 'md:opacity-0 md:w-0' : 'md:opacity-100')} style={{ display: sidebarCollapsed ? 'none' : 'block', transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)'}}>Logout</span>
            <svg className={classNames('h-5 w-5 transition-all duration-300', sidebarCollapsed ? 'md:opacity-100' : 'md:opacity-0 md:w-0')} style={{ display: sidebarCollapsed ? 'block' : 'none', transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
          </button>
        )}
      </div>
      </div>

      {/* Main Content */}
      <div className={`flex flex-1 flex-col ${noScroll ? 'min-h-0 overflow-y-hidden' : 'overflow-y-auto'}`}>
        {/* Only show alert banner if a lockout is active */}
        
          <AlertBanner 
            subscriptionInfo={subscriptionInfo}
            onUpgrade={handleUpgrade}
            onManageMembers={handleManageMembers}
            onUpdatePayment={handleUpgrade}
          />
        
        
        {userLoading ? (
          <main className="flex-1 bg-gray-100 p-6 flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 border-4 border-gray-300 border-t-yellow-500 rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Loading your data...</p>
            </div>
          </main>
        ) : shouldLockout ? (
          // <--- UPDATED: Conditional lockout logic based on user role
          <main className="flex-1 bg-gray-100 p-6 flex items-center justify-center">
            <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl transition-all duration-300 hover:shadow-2xl">
              {/* Header with Icon */}
              <div className="flex flex-col items-center justify-center p-8 text-center bg-yellow-500 text-black">
                <svg className="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="mt-4 text-3xl font-extrabold">Access Suspended</h2>
              </div>
              
              {role === 'admin' || role === 'owner' ? (
                // CONTENT FOR ADMINS/OWNERS
                <div className="p-8 text-center">
                  <p className="mt-2 text-lg text-gray-700">
                    {subscriptionInfo?.status === 'trialing' ? 
                      "Your free trial has ended. To continue managing your projects and team, please choose a plan that's right for you." : 
                      "Your subscription payment failed. To restore full access to your account, please update your payment method."
                    }
                  </p>
                  <button
                    onClick={handleUpgrade}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    {subscriptionInfo?.status === 'trialing' ? 'Choose a Plan' : 'Update Payment'}
                  </button>
                  <p className="mt-6 text-sm text-gray-500">
                    Have questions or concerns? Please reach out to our team.
                    <br />
                    <a href="mailto:connect@transtechsys.com" className="font-medium text-blue-600 hover:text-blue-500 hover:underline">
                      connect@transtechsys.com
                    </a>
                  </p>
                </div>
              ) : (
                // CONTENT FOR REGULAR MEMBERS
                <div className="p-8 text-center">
                  <p className="mt-2 text-lg text-gray-700">
                    Your organization's access has been suspended due to an issue with the subscription.
                  </p>
                  <p className="mt-4 text-sm text-gray-500">
                    Please contact an administrator or account owner to resolve this issue.
                  </p>
                  <button
                    onClick={handleLogout}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Logout
                  </button>
                  <p className="mt-6 text-sm text-gray-500">
                    If you are an administrator, please log in with the correct account.
                  </p>
                </div>
              )}
            </div>
          </main>
        ) : (
          <main className={`flex-1 bg-gray-100 ${noPadding ? '' : 'p-4 pt-20 md:pt-6'} ${noScroll ? 'overflow-y-hidden overflow-x-hidden' : 'overflow-x-hidden'}`}>
            {children}
          </main>
        )}
      </div>

      {/* Portal for Tooltips */}
      {hoveredTooltip && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed bg-gray-900 text-white text-sm rounded-md px-3 py-2 shadow-xl whitespace-nowrap border border-gray-600 pointer-events-none z-[9999] transition-opacity duration-200"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {hoveredTooltip.text}
          {hoveredTooltip.subtitle && (
            <div className="text-xs text-gray-400 mt-1">
              {hoveredTooltip.subtitle}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}